// ExportView — a package (export) resource's own full view, pure. Composes
// Heading, Badge, Mono, Text, Button, ProgressBar.
//
// The persistent NOUN beside ExportFlow's guided VERB: the flow walks the
// author from format choice to file; this view is where an already-created
// package lives afterwards — how the build stands and the download, what it
// was made from (the material, linked, and its sources), what the format
// leaves out, and the files the download delivers (disclosed on demand). It shares ExportFlow's vocabulary STYLE (the
// status is a sum with one variant per moment; degradation is named, never
// silent) but owns its types — a resource view outlives any one flow, so it
// does not import from it.
//
// `ExportStatus` makes the impossible mixes unrepresentable: building
// carries nothing yet; ready carries the file name and the findings that
// survived validation; failed carries the author-facing reason plus whatever
// findings were gathered before the build stopped. Findings are a sum by
// severity (note / warning / problem), each its own named variant, rendered
// through the Badge discipline — status tones demand an icon, so severity
// never rides on colour alone.
//
// Pure/props-driven: the status arrives as a prop and every transition
// happens elsewhere; the one intent this view reports is `onDownload`
// (offered only while ready — the accent's single spend here).

import { exhaustSwitchCase } from "@bilda/utils";
import { Plural, Trans, useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Badge } from "../Badge/Badge.js";
import { Button } from "../Button/Button.js";
import { Heading } from "../Heading/Heading.js";
import { Icon } from "../Icon/Icon.js";
import { Mono } from "../Mono/Mono.js";
import type { MaterialRef, SourceRef } from "../Navigation/types.js";
import { ProgressBar } from "../ProgressBar/ProgressBar.js";
import { Text } from "../Text/Text.js";
import "./ExportView.css";

// The package's format as DATA — the view owns the author-facing words.
// `other` carries an unrecognised packaging id, shown raw (dev-jargon fallback,
// better than guessing); `unspecified` is a package whose Packaging is unknown.
export type ExportFormatKind =
    | { readonly type: "torus" }
    | { readonly type: "canvas" }
    | { readonly type: "markdown" }
    | { readonly type: "html" }
    | { readonly type: "text" }
    | { readonly type: "other"; readonly id: string }
    | { readonly type: "unspecified" };

// What the package was made from. `material` names the material (at the
// version packaged) plus that material's own sources; `removed-material` is a
// material deleted since (named, no longer openable); `unrecorded` is an older
// package made before its origin was written down.
export type PackagedFrom =
    | {
          readonly type: "material";
          readonly material: MaterialRef;
          readonly version: number;
          readonly sources: readonly SourceRef[];
      }
    | {
          readonly type: "removed-material";
          readonly title: string;
          readonly version: number;
      }
    | { readonly type: "unrecorded" };

// What the target format could not carry for THIS package — status, not an
// alarm: nothing to fix, only what to expect. The view owns the words; an
// unrecognised tag travels raw as `other`. The `part-*` / `source-*` lines
// come from export resolving the material's included parts: what it could
// not include, and the embedded sources to check.
export type FormatLoss =
    | { readonly type: "interactivity" }
    | { readonly type: "styling" }
    | { readonly type: "grading" }
    | { readonly type: "audio-details" }
    | { readonly type: "objective-alignment" }
    | { readonly type: "part-removed"; readonly title: string }
    | { readonly type: "part-unavailable"; readonly title: string }
    | { readonly type: "part-planned"; readonly title: string }
    | { readonly type: "part-unreviewed"; readonly title: string }
    | { readonly type: "part-not-includable"; readonly title: string }
    | { readonly type: "part-loop"; readonly title: string }
    | { readonly type: "part-not-in-module"; readonly title: string }
    | { readonly type: "container-flattened"; readonly title: string }
    | { readonly type: "source-not-attached"; readonly title: string }
    | { readonly type: "source-rights"; readonly title: string }
    | { readonly type: "other"; readonly id: string };

// One file the download delivers, named as the download names it.
export type PackageFile = { readonly name: string };

export type ExportContents = {
    readonly format: ExportFormatKind;
    readonly packagedFrom: PackagedFrom;
    readonly leftOut: readonly FormatLoss[];
    readonly files: readonly PackageFile[];
};

// === Validation findings — a sum by severity ================================
// Each severity is its own named variant (not one shape with a severity
// enum-field to branch on): the dispatch below is the discriminant's job.
export type NoteFinding = {
    readonly type: "note";
    readonly message: string;
};
export type WarningFinding = {
    readonly type: "warning";
    readonly message: string; // e.g. "Video degraded to a link"
};
export type ProblemFinding = {
    readonly type: "problem";
    readonly message: string;
};
type ValidationFinding = NoteFinding | WarningFinding | ProblemFinding;

// === Status — one variant per moment ========================================
export type ExportBuilding = { readonly type: "building" };
export type ExportReady = {
    readonly type: "ready";
    // Absent when the package is untitled (nothing distinct to show).
    readonly downloadName?: string;
    readonly findings: readonly ValidationFinding[];
};
export type ExportFailed = {
    readonly type: "failed";
    // Author-facing reason the build stopped.
    readonly message: string;
    readonly findings: readonly ValidationFinding[];
};
export type ExportStatus = ExportBuilding | ExportReady | ExportFailed;

export type ExportViewProps = {
    // The package resource's title.
    readonly title: string;
    readonly contents: ExportContents;
    readonly status: ExportStatus;
    // Fetch the built file — offered only while `ready`.
    readonly onDownload?: () => void;
    // Follow a lineage link (the packaged material, its sources) — the host's
    // `openRef`. Absent ⇒ the names read as plain text.
    readonly onOpenRef?: (ref: MaterialRef | SourceRef) => void;
    // The package's lifecycle overflow (a composed RowOverflow — master
    // audit R5), a slot so the caller owns the action menu.
    readonly overflow?: ReactNode;
    readonly className?: string;
};

// A filled check — the ready line's redundant (non-colour) channel.
const readyGlyph = <Icon name="check" />;

// A triangle-alert — failure's and a warning's redundant channel.
const alertGlyph = <Icon name="warning" />;

// One finding row — a total match over the severity discriminant. `note` is a
// label tone (no status meaning → no icon demanded); warning/problem are
// status tones, so the Badge type itself requires the redundant icon.
const FindingRow = ({
    finding,
}: {
    readonly finding: ValidationFinding;
}): ReactNode => {
    switch (finding.type) {
        case "note":
            return (
                <li className="bilda-export-view__finding">
                    <Badge>
                        <Trans>Note</Trans>
                    </Badge>
                    <Text size="sm">{finding.message}</Text>
                </li>
            );
        case "warning":
            return (
                <li className="bilda-export-view__finding">
                    <Badge tone="warning" icon={alertGlyph}>
                        <Trans>Warning</Trans>
                    </Badge>
                    <Text size="sm">{finding.message}</Text>
                </li>
            );
        case "problem":
            return (
                <li className="bilda-export-view__finding">
                    <Badge tone="danger" icon={alertGlyph}>
                        <Trans>Problem</Trans>
                    </Badge>
                    <Text size="sm">{finding.message}</Text>
                </li>
            );
        default:
            return exhaustSwitchCase(finding);
    }
};

// The findings section — shared by ready and failed; silent when empty.
const Findings = ({
    findings,
}: {
    readonly findings: readonly ValidationFinding[];
}): ReactNode =>
    findings.length === 0 ? null : (
        <section className="bilda-export-view__section">
            <Heading level={2} size="lg" tone="dim">
                <Trans>Packaging notes</Trans>
            </Heading>
            <ul className="bilda-export-view__findings">
                {findings.map((finding) => (
                    <FindingRow
                        key={`${finding.type}-${finding.message}`}
                        finding={finding}
                    />
                ))}
            </ul>
        </section>
    );

// A lineage name: a quiet link when the host can open it, else plain text.
const RefName = ({
    target,
    icon,
    onOpenRef,
}: {
    readonly target: MaterialRef | SourceRef;
    readonly icon: "material" | "source";
    readonly onOpenRef?: (ref: MaterialRef | SourceRef) => void;
}): ReactNode =>
    onOpenRef !== undefined ? (
        <Button variant="ghost" size="sm" onClick={() => onOpenRef(target)}>
            <Icon name={icon} size="sm" /> {target.title}
        </Button>
    ) : (
        <Text as="span" size="sm">
            {target.title}
        </Text>
    );

// "Made from" — the package's lineage, a total match over what is known.
const MadeFromBody = ({
    packagedFrom,
    onOpenRef,
}: {
    readonly packagedFrom: PackagedFrom;
    readonly onOpenRef?: (ref: MaterialRef | SourceRef) => void;
}): ReactNode => {
    switch (packagedFrom.type) {
        case "material": {
            const { material, version, sources } = packagedFrom;
            return (
                <>
                    <p className="bilda-export-view__lineage">
                        <RefName
                            target={material}
                            icon="material"
                            onOpenRef={onOpenRef}
                        />
                        <Text as="span" size="sm" tone="dim">
                            <Trans>version {version}</Trans>
                        </Text>
                    </p>
                    {sources.length > 0 ? (
                        <p className="bilda-export-view__lineage">
                            <Text as="span" size="sm" tone="dim">
                                <Trans>Sources:</Trans>
                            </Text>
                            {sources.map((source) => (
                                <RefName
                                    key={source.id}
                                    target={source}
                                    icon="source"
                                    onOpenRef={onOpenRef}
                                />
                            ))}
                        </p>
                    ) : null}
                </>
            );
        }
        case "removed-material":
            return (
                <p className="bilda-export-view__lineage">
                    <Text as="span" size="sm">
                        {packagedFrom.title}
                    </Text>
                    <Text as="span" size="sm" tone="dim">
                        <Trans>
                            version {packagedFrom.version} · since removed
                        </Trans>
                    </Text>
                </p>
            );
        case "unrecorded":
            return (
                <Text as="p" size="sm" tone="dim">
                    <Trans>
                        Not recorded: this package was made before the app noted
                        which material it came from.
                    </Trans>
                </Text>
            );
        default:
            return exhaustSwitchCase(packagedFrom);
    }
};

// The plain-language line for one format loss — the one place the words live.
const useFormatLossLine = (): ((loss: FormatLoss) => string) => {
    const { t } = useLingui();
    return (loss) => {
        switch (loss.type) {
            case "interactivity":
                return t`Interactive parts become static.`;
            case "styling":
                return t`Styling is simplified.`;
            case "grading":
                return t`Automatic grading isn't included.`;
            case "audio-details":
                return t`Audio details are left out.`;
            case "objective-alignment":
                return t`Links between questions and learning objectives are left out.`;
            case "part-removed":
                return t`"${loss.title}" was removed, so it isn't included.`;
            case "part-unavailable":
                return t`"${loss.title}" couldn't be read at its saved version, so it isn't included.`;
            case "part-planned":
                return t`"${loss.title}" isn't written yet, so it isn't included.`;
            case "part-unreviewed":
                return t`"${loss.title}" is included but not reviewed yet.`;
            case "part-not-includable":
                return t`"${loss.title}" is a package and can't go inside another one.`;
            case "part-loop":
                return t`"${loss.title}" includes itself; the repeat is left out.`;
            case "part-not-in-module":
                return t`"${loss.title}" isn't in a module; it's exported where it stands.`;
            case "container-flattened":
                return t`"${loss.title}" can't be its own module in this format; it's a heading inside the module above.`;
            case "source-not-attached":
                return t`The source "${loss.title}" isn't attached; the package names it instead.`;
            case "source-rights":
                return t`Embedded source "${loss.title}": check the rights.`;
            case "other":
                return loss.id;
            default:
                return exhaustSwitchCase(loss);
        }
    };
};

// What the format leaves out — a calm list (status, not a warning: nothing
// to act on). Silent when nothing was lost.
const LeftOut = ({
    leftOut,
}: {
    readonly leftOut: readonly FormatLoss[];
}): ReactNode => {
    const line = useFormatLossLine();
    return leftOut.length === 0 ? null : (
        <section className="bilda-export-view__section">
            <Heading level={2} size="lg" tone="dim">
                <Trans>What this format leaves out</Trans>
            </Heading>
            <ul className="bilda-export-view__left-out">
                {leftOut.map((loss, i) => (
                    // Several lines can share a type (one per part); the
                    // list is static, so the position is a stable key.
                    // biome-ignore lint/suspicious/noArrayIndexKey: static list
                    <li key={`${loss.type}-${i}`}>
                        <Text size="sm">{line(loss)}</Text>
                    </li>
                ))}
            </ul>
        </section>
    );
};

// The download's file list, behind progressive disclosure (closed by default).
const Files = ({
    files,
}: {
    readonly files: readonly PackageFile[];
}): ReactNode =>
    files.length === 0 ? null : (
        <details className="bilda-export-view__files">
            <summary>
                <Text as="span" size="sm" tone="dim">
                    <Plural
                        value={files.length}
                        one="# file in the download"
                        other="# files in the download"
                    />
                </Text>
            </summary>
            <ul>
                {files.map((file) => (
                    <li key={file.name}>
                        <Mono>{file.name}</Mono>
                    </li>
                ))}
            </ul>
        </details>
    );

// The status block — a total match over the moment.
const StatusView = ({
    status,
    title,
    onDownload,
}: {
    readonly status: ExportStatus;
    // The package's own title — used to suppress a download-name line that
    // would just repeat it (each element one job).
    readonly title: string;
    readonly onDownload?: () => void;
}): ReactNode => {
    const { t } = useLingui();
    switch (status.type) {
        case "building":
            return (
                <div className="bilda-export-view__status" role="status">
                    <ProgressBar label={t`Building`} />
                    <Text tone="dim">
                        <Trans>Building the package…</Trans>
                    </Text>
                </div>
            );
        case "ready":
            return (
                <>
                    <p className="bilda-export-view__line">
                        <Text tone="success">
                            <span
                                className="bilda-export-view__glyph"
                                aria-hidden="true"
                            >
                                {readyGlyph}
                            </span>
                            <Trans>Your package is ready.</Trans>
                        </Text>
                        {/* The download filename adds nothing when it is just
                            the package title; show it only when it is a real,
                            distinct filename (e.g. the mock's *.torus.zip). */}
                        {status.downloadName !== undefined &&
                        status.downloadName !== title ? (
                            <Mono>{status.downloadName}</Mono>
                        ) : null}
                    </p>
                    <Findings findings={status.findings} />
                    {onDownload ? (
                        <div className="bilda-export-view__actions">
                            <Button variant="primary" onClick={onDownload}>
                                <Trans>Download package</Trans>
                            </Button>
                        </div>
                    ) : null}
                </>
            );
        case "failed":
            return (
                <>
                    <p className="bilda-export-view__line">
                        <Text tone="danger">
                            <span
                                className="bilda-export-view__glyph"
                                aria-hidden="true"
                            >
                                {alertGlyph}
                            </span>
                            {status.message}
                        </Text>
                    </p>
                    <Findings findings={status.findings} />
                </>
            );
        default:
            return exhaustSwitchCase(status);
    }
};

// The author-facing name of a package format — the one place the words live.
const useExportFormatName = (): ((f: ExportFormatKind) => string) => {
    const { t } = useLingui();
    return (f) => {
        switch (f.type) {
            case "torus":
                return t`Torus package`;
            case "canvas":
                return t`Common Cartridge`;
            case "markdown":
                return t`Markdown`;
            case "html":
                return t`HTML`;
            case "text":
                return t`Text`;
            case "other":
                return f.id;
            case "unspecified":
                return t({ message: "Export", context: "resource kind" });
            default:
                return exhaustSwitchCase(f);
        }
    };
};

export const ExportView = ({
    title,
    contents,
    status,
    onDownload,
    onOpenRef,
    overflow,
    className,
}: ExportViewProps) => {
    const formatName = useExportFormatName()(contents.format);
    return (
        <div className={`bilda-export-view${className ? ` ${className}` : ""}`}>
            <header className="bilda-export-view__header">
                <div className="bilda-export-view__title-row">
                    <Heading
                        level={1}
                        size="2xl"
                        className="bilda-heading--clamp"
                        title={title}
                    >
                        {title}
                    </Heading>
                    {overflow}
                </div>
                {/* How it was built; WHAT it was built from is the "Made
                from" section below. (A packaged-at date is a projection gap
                today; the line stays honest without it.) */}
                <p className="bilda-export-view__contents">
                    <Text size="sm" tone="dim">
                        <Trans>Packaged · {formatName}</Trans>
                    </Text>
                </p>
            </header>
            <StatusView status={status} title={title} onDownload={onDownload} />
            <section className="bilda-export-view__section">
                <Heading level={2} size="lg" tone="dim">
                    <Trans>Made from</Trans>
                </Heading>
                <MadeFromBody
                    packagedFrom={contents.packagedFrom}
                    onOpenRef={onOpenRef}
                />
            </section>
            <LeftOut leftOut={contents.leftOut} />
            <Files files={contents.files} />
        </div>
    );
};
